import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../utils/api'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300

/**
 * Customer search — the way in to one person's record.
 *
 * Search-first rather than a browsable list. There is no useful "all customers"
 * page: nobody opens this screen wondering who exists, they open it holding a
 * name, an address or a phone number from an email or a phone call.
 */
function Customers() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)

  const debounceTimer = useRef(null)
  // Every search carries a sequence number, so a slow early request cannot
  // land after a fast later one and repaint the list with stale matches.
  const requestSeq = useRef(0)

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([])
      setHasSearched(false)
      setError(null)
      return
    }

    debounceTimer.current = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS)
    return () => clearTimeout(debounceTimer.current)
  }, [query])

  async function runSearch(term) {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const data = await api.get(`/api/customers/search?q=${encodeURIComponent(term)}`)
      if (seq !== requestSeq.current) return
      setResults(data.customers || [])
      setHasSearched(true)
    } catch (e) {
      if (seq !== requestSeq.current) return
      setError(e.message || 'Could not search customers')
      setResults([])
      setHasSearched(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  function nameOf(customer) {
    const pii = customer.pii || {}
    const name = [pii.first_name, pii.last_name].filter(Boolean).join(' ')
    return name || '(no name on record)'
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Customers</h1>
        <p className="page-description">
          Search by name, email or phone to see someone's deals, their email
          settings and how to reach them.
        </p>
      </div>

      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div className="filter-section">
          <div
            className="filter-group filter-group-wide"
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
          >
            <label className="filter-label">Search (name, email or phone)</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. jane smith, jane@example.com, 07700…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && query.trim().length >= MIN_QUERY_LENGTH) {
                  if (debounceTimer.current) clearTimeout(debounceTimer.current)
                  runSearch(query.trim())
                }
              }}
              autoFocus
            />
          </div>
        </div>
      </div>

      {loading && <div className="card" style={{ padding: 'var(--space-4)' }}>Searching…</div>}

      {error && !loading && (
        <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-danger)' }}>
          {error}
        </div>
      )}

      {hasSearched && !loading && !error && results.length === 0 && (
        <div className="card empty-state">
          <div className="empty-state-title">No customers found</div>
          <div className="empty-state-description">
            Try part of a surname, or the email address they used.
          </div>
        </div>
      )}

      {!hasSearched && !loading && !error && (
        <div className="card empty-state">
          <div className="empty-state-title">Search for a customer</div>
          <div className="empty-state-description">
            Type at least {MIN_QUERY_LENGTH} characters. A full email address
            matches exactly; anything else matches names, emails and phones.
          </div>
        </div>
      )}

      {results.length > 0 && !loading && (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Postcode</th>
              </tr>
            </thead>
            <tbody>
              {results.map(customer => (
                <tr
                  key={customer.customer_id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/customers/${customer.customer_id}`)}
                >
                  <td>{nameOf(customer)}</td>
                  <td>{customer.pii?.email || '-'}</td>
                  <td>{customer.pii?.phone || '-'}</td>
                  <td>{customer.pii?.postcode || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default Customers
